import { Card, DownloadRow, Skeleton } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { useAsync } from '@/lib/useAsync'
import { checkoutService } from '@/services/checkout'
import type { AfterStay } from '@/services/checkout'
import { fianzaService } from '@/services/fianza'
import { useApiLang } from '../Checkin/useSave'
import { DepositAfterCard } from '../Estancia/sections/DepositCards'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'
import { RebookCard, ReviewCard } from './shared'

const RECEIPTS_ROUTE = '/reserva/recibos'

/**
 * 8.11 · Después de tu estancia (Cierre.dc.html post, Bloque 7 §7b): la fianza primero (seguimiento del bloque 3),
 * factura descargable y recibos (bloque 3), reseña en el canal, volver a reservar y fidelización si la hay.
 */
export function Despues() {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useLoadedStay()
  const [deposit] = useAsync(`despues-fianza:${stay.guestCode}:${apiLang}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const [after, reload] = useAsync(`despues:${stay.guestCode}`, (signal) => checkoutService.getAfterStay(stay, signal))
  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to="/" label={t('v2.ds.nav.estancia')} />
        <h1>{t('v2.despues.titulo')}</h1>
        <p className="page__lead">{t('v2.despues.lead')}</p>
      </div>
      <DepositAfterCard deposit={deposit} lang={lang} origin="despues" />
      {stay.bills.length > 0 && (
        <Card flush>
          {stay.bills.map((b) => (
            <DownloadRow key={b.id} title={t('v2.despues.factura', { id: b.id.replace(/^F-/, '') })} href={b.url} />
          ))}
        </Card>
      )}
      {(stay.bills.length > 0 || stay.flags.showGetBills) && (
        <LinkRow icon="file-text" title={stay.bills.length > 0 ? t('v2.despues.recibos') : t('v2.salida.facturaRecibos')} {...(stay.bills.length > 0 ? {} : { subtitle: t('v2.despues.pedirFactura') })} to={RECEIPTS_ROUTE} />
      )}
      {after.kind === 'loading' && (
        <div className="pagos__stack" aria-busy="true">
          <Skeleton height={140} />
          <Skeleton height={200} />
        </div>
      )}
      {after.kind === 'error' && <SectionError code={after.code} onRetry={reload} />}
      {after.kind === 'ready' && <AfterContent after={after.value} />}
    </main>
  )
}

function AfterContent({ after }: { after: AfterStay }) {
  const { t } = useLang()
  const loyalty = after.loyalty
  return (
    <>
      <ReviewCard review={after.review} />
      <RebookCard rebook={after.rebook} />
      {loyalty && (
        <Card as="section">
          <div className="cierre__card">
            <span className="cierre__caption">{loyalty.levelName}</span>
            <h2 className="cierre__card-title">{t('v2.despues.fidelizacion.titulo', { done: loyalty.staysDone, needed: loyalty.staysNeeded, level: loyalty.nextLevel })}</h2>
            <p className="cierre__note">{t('v2.despues.fidelizacion.texto', { level: loyalty.nextLevel, perks: loyalty.nextLevelPerks })}</p>
          </div>
        </Card>
      )}
    </>
  )
}
